import { useCallback, useEffect, useMemo, useRef } from 'react'

import { SECTION_ID } from '../../shared/constants/sectionIds'

function parseSectionScrollOffset(offset) {
  if (offset == null) return 0
  if (typeof offset === 'number' && Number.isFinite(offset)) {
    return Math.max(0, offset)
  }

  const trimmedOffset = String(offset).trim()
  if (!trimmedOffset) return 0

  if (/px$/i.test(trimmedOffset)) {
    const value = Number.parseFloat(trimmedOffset)
    return Number.isFinite(value) ? Math.max(0, value) : 0
  }

  if (/rem$/i.test(trimmedOffset)) {
    const value = Number.parseFloat(trimmedOffset)
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return Number.isFinite(value) ? Math.max(0, value * rootFontSize) : 0
  }

  const value = Number.parseFloat(trimmedOffset)
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

function resolveSectionScrollOffset(targetElement, options = {}) {
  const resolvedOptions = options ?? {}

  if (resolvedOptions.offset != null) return parseSectionScrollOffset(resolvedOptions.offset)

  if (!resolvedOptions.offsetCssVar) return 0

  const rootElement = resolvedOptions.offsetRoot ?? targetElement.closest('.bim-app-root') ?? document.documentElement
  return parseSectionScrollOffset(getComputedStyle(rootElement).getPropertyValue(resolvedOptions.offsetCssVar))
}

export function scrollElementIntoView(targetElement, options = {}) {
  if (!targetElement) return

  const offset = resolveSectionScrollOffset(targetElement, options)
  if (offset <= 0) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const top = window.scrollY + targetElement.getBoundingClientRect().top - offset
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

export function useAppSectionRegistry() {
  const pendingScrollRafRef = useRef(null)
  const heroRef = useRef(null)
  const catalogRef = useRef(null)
  const tiersRef = useRef(null)
  const productRef = useRef(null)
  const favoritesRef = useRef(null)

  const sectionRefs = useMemo(
    () => ({
      [SECTION_ID.HERO]: heroRef,
      [SECTION_ID.CATALOG]: catalogRef,
      [SECTION_ID.TIERS]: tiersRef,
      [SECTION_ID.PRODUCT]: productRef,
      [SECTION_ID.FAVORITES]: favoritesRef,
    }),
    [],
  )

  const scrollToSection = useCallback((name, options) => {
    scrollElementIntoView(sectionRefs[name]?.current, options)
  }, [sectionRefs])

  const scrollToSectionWhenReady = useCallback((name, maxFrames = 10, options) => {
    if (pendingScrollRafRef.current) {
      cancelAnimationFrame(pendingScrollRafRef.current)
      pendingScrollRafRef.current = null
    }

    let framesRemaining = maxFrames

    const tryScroll = () => {
      if (sectionRefs[name]?.current) {
        scrollToSection(name, options)
        pendingScrollRafRef.current = null
        return
      }

      framesRemaining -= 1
      if (framesRemaining <= 0) {
        pendingScrollRafRef.current = null
        return
      }

      pendingScrollRafRef.current = requestAnimationFrame(tryScroll)
    }

    pendingScrollRafRef.current = requestAnimationFrame(tryScroll)
  }, [scrollToSection, sectionRefs])

  useEffect(() => {
    return () => {
      if (pendingScrollRafRef.current) {
        cancelAnimationFrame(pendingScrollRafRef.current)
      }
    }
  }, [])

  return {
    sectionRefs,
    scrollToSection,
    scrollToSectionWhenReady,
  }
}